import { createSlice, createSelector } from '@reduxjs/toolkit';

/**
 * Stripe product catalog – maps each onboarding capability to its Stripe price.
 * Structure mirrors the billing POC:
 *   - priceId:      Stripe Price ID (from the test catalog)
 *   - name:         Human-readable name
 *   - unitAmountUsd: Per-unit daily cost (recurring) or flat cost (one-time), in USD dollars
 *   - recurring:     true = daily subscription, false = one-time charge
 *   - miraFte:       true if this capability's quantity scales with Mira FTE count
 *   - frankFte:      true if this capability's quantity scales with Frank FTE count
 *   - category:      Stripe product category for grouping
 */
export const STRIPE_CAPABILITY_CATALOG = {
  appointments: {
    priceId: 'price_appointments_daily',
    name: 'Appointments',
    unitAmountUsd: 8.50,
    recurring: true,
    miraFte: false,
    frankFte: true,
    category: 'Admin Operations',
  },
  medications: {
    priceId: 'price_medications_daily',
    name: 'Medications / Pharmacy',
    unitAmountUsd: 7.00,
    recurring: true,
    miraFte: false,
    frankFte: true,
    category: 'Admin Operations',
  },
  medicalIssues: {
    priceId: 'price_medical_issues_daily',
    name: 'Medical Issues (Triage Assist)',
    unitAmountUsd: 12.00,
    recurring: true,
    miraFte: true,
    frankFte: false,
    category: 'Clinical',
  },
  billingInsurance: {
    priceId: 'price_billing_insurance_daily',
    name: 'Billing / Insurance',
    unitAmountUsd: 6.50,
    recurring: true,
    miraFte: false,
    frankFte: true,
    category: 'Admin Operations',
  },
  medicalRecords: {
    priceId: 'price_medical_records_daily',
    name: 'Medical Records',
    unitAmountUsd: 5.00,
    recurring: true,
    miraFte: true,
    frankFte: false,
    category: 'Clinical',
  },
  generalInquiries: {
    priceId: 'price_general_inquiries_daily',
    name: 'General Inquiries',
    unitAmountUsd: 4.00,
    recurring: true,
    miraFte: true,
    frankFte: true,
    category: 'General',
  },
  providerCalls: {
    priceId: 'price_provider_calls_daily',
    name: 'Provider Calls',
    unitAmountUsd: 9.00,
    recurring: true,
    miraFte: true,
    frankFte: true,
    category: 'Clinical',
  },
  vendorCalls: {
    priceId: 'price_vendor_calls_daily',
    name: 'Vendor Calls',
    unitAmountUsd: 3.50,
    recurring: true,
    miraFte: false,
    frankFte: true,
    category: 'Admin Operations',
  },
};

/** One-time setup fee per category (mirrors POC auto-included setup fees) */
export const STRIPE_SETUP_FEES = {
  'Admin Operations': {
    priceId: 'price_setup_admin_ops',
    name: 'Admin Operations Setup',
    unitAmountUsd: 500.00,
    recurring: false,
  },
  'Clinical': {
    priceId: 'price_setup_clinical',
    name: 'Clinical Setup',
    unitAmountUsd: 750.00,
    recurring: false,
  },
  'General': {
    priceId: 'price_setup_general',
    name: 'General Setup',
    unitAmountUsd: 250.00,
    recurring: false,
  },
};

const initialState = {
  currentView: 0,
  miraFTE: '1',
  frankFTE: '1',
  organizationName: '',
  selectedOption: null,
  email: '',
  officeNumber: '',
  emailError: '',
  isEmailValid: false,
  orgSearchResults: [],
  isSearchingOrgs: false,
  showOrgSuggestions: false,
  selectedOrgId: null,
  providerSearchResults: [],
  isSearchingProviders: false,
  showProviderSuggestions: false,
  selectedProvider: null,
  // Cart: keyed by capability ID, value = true/false (selected)
  cartCapabilities: {},
  // Tax rate for payment calculation
  taxRate: 0.05,
};

const signupSlice = createSlice({
  name: 'signup',
  initialState,
  reducers: {
    // View management
    setCurrentView: (state, action) => {
      state.currentView = action.payload;
    },
    
    // FTE management
    setMiraFTE: (state, action) => {
      state.miraFTE = action.payload;
    },
    
    setFrankFTE: (state, action) => {
      state.frankFTE = action.payload;
    },
    
    // Organization management
    setOrganizationName: (state, action) => {
      state.organizationName = action.payload;
    },
    
    setSelectedOption: (state, action) => {
      state.selectedOption = action.payload;
    },
    
    setSelectedOrgId: (state, action) => {
      state.selectedOrgId = action.payload;
    },
    
    // Organization search
    setOrgSearchResults: (state, action) => {
      state.orgSearchResults = action.payload;
    },
    
    setIsSearchingOrgs: (state, action) => {
      state.isSearchingOrgs = action.payload;
    },
    
    setShowOrgSuggestions: (state, action) => {
      state.showOrgSuggestions = action.payload;
    },
    
    // Email management
    setEmail: (state, action) => {
      state.email = action.payload;
    },
    
    setEmailError: (state, action) => {
      state.emailError = action.payload;
    },
    
    setIsEmailValid: (state, action) => {
      state.isEmailValid = action.payload;
    },
    
    setOfficeNumber: (state, action) => {
      state.officeNumber = action.payload;
    },
    
    // Provider search
    setProviderSearchResults: (state, action) => {
      state.providerSearchResults = action.payload;
    },
    
    setIsSearchingProviders: (state, action) => {
      state.isSearchingProviders = action.payload;
    },
    
    setShowProviderSuggestions: (state, action) => {
      state.showProviderSuggestions = action.payload;
    },
    
    setSelectedProvider: (state, action) => {
      state.selectedProvider = action.payload;
    },
    
    // Cart management
    setCartCapabilities: (state, action) => {
      state.cartCapabilities = action.payload;
    },
    
    toggleCartCapability: (state, action) => {
      const capId = action.payload;
      state.cartCapabilities[capId] = !state.cartCapabilities[capId];
    },
    
    setTaxRate: (state, action) => {
      state.taxRate = action.payload;
    },
    
    // Reset signup data
    resetSignupData: (state) => {
      return { ...initialState };
    },
    
    // Get complete signup data for submission
    getSignupData: (state) => {
      return {
        selectedOption: state.selectedOption,
        miraFTE: state.miraFTE,
        frankFTE: state.frankFTE,
        organizationName: state.organizationName,
        selectedOrgId: state.selectedOrgId,
        email: state.email,
        officeNumber: state.officeNumber,
      };
    },
  },
});

// Action creators
export const {
  setCurrentView,
  setMiraFTE,
  setFrankFTE,
  setOrganizationName,
  setSelectedOption,
  setSelectedOrgId,
  setOrgSearchResults,
  setIsSearchingOrgs,
  setShowOrgSuggestions,
  setEmail,
  setEmailError,
  setIsEmailValid,
  setOfficeNumber,
  setProviderSearchResults,
  setIsSearchingProviders,
  setShowProviderSuggestions,
  setSelectedProvider,
  setCartCapabilities,
  toggleCartCapability,
  setTaxRate,
  resetSignupData,
} = signupSlice.actions;

// Selectors
export const selectCurrentView = (state) => state.signup.currentView;
export const selectMiraFTE = (state) => state.signup.miraFTE;
export const selectFrankFTE = (state) => state.signup.frankFTE;
export const selectOrganizationName = (state) => state.signup.organizationName;
export const selectSelectedOption = (state) => state.signup.selectedOption;
export const selectEmail = (state) => state.signup.email;
export const selectOfficeNumber = (state) => state.signup.officeNumber;
export const selectEmailError = (state) => state.signup.emailError;
export const selectIsEmailValid = (state) => state.signup.isEmailValid;
export const selectOrgSearchResults = (state) => state.signup.orgSearchResults;
export const selectIsSearchingOrgs = (state) => state.signup.isSearchingOrgs;
export const selectShowOrgSuggestions = (state) => state.signup.showOrgSuggestions;
export const selectSelectedOrgId = (state) => state.signup.selectedOrgId;
export const selectProviderSearchResults = (state) => state.signup.providerSearchResults;
export const selectIsSearchingProviders = (state) => state.signup.isSearchingProviders;
export const selectShowProviderSuggestions = (state) => state.signup.showProviderSuggestions;
export const selectSelectedProvider = (state) => state.signup.selectedProvider;
export const selectCartCapabilities = (state) => state.signup.cartCapabilities;
export const selectTaxRate = (state) => state.signup.taxRate;

/**
 * Derived selector: computes the full cart with line items and totals.
 * Mirrors the POC's renderCart() / checkout() logic:
 *   - Recurring items: dailyCost = unitAmountUsd × fteQty(miraFte, frankFte)
 *   - One-time items (setup fees): auto-included per category that has at least one selected capability
 *   - Totals: oneTimeTotal, dailyRecurringTotal, monthlyEstimate, tax, grandTotal
 */
export const selectCartDetails = createSelector(
  [
    selectCartCapabilities,
    selectMiraFTE,
    selectFrankFTE,
    selectTaxRate,
  ],
  (cartCapabilities, miraFTE, frankFTE, taxRate) => {
    const safeTaxRate = typeof taxRate === 'number' ? taxRate : 0.05;
    const miraFteNum = Math.max(1, parseInt(miraFTE, 10) || 1);
    const frankFteNum = Math.max(1, parseInt(frankFTE, 10) || 1);

    // Helper: compute quantity based on FTE flags (same as POC's fteQty)
    const fteQty = (miraFlag, frankFlag) => {
      let qty = 0;
      if (miraFlag) qty += miraFteNum;
      if (frankFlag) qty += frankFteNum;
      return Math.max(1, qty);
    };

    const fteLabel = (miraFlag, frankFlag) => {
      if (miraFlag && frankFlag) return 'Mira + Frank';
      if (miraFlag) return 'Mira';
      if (frankFlag) return 'Frank';
      return '';
    };

    const lineItems = [];
    let dailyRecurringTotal = 0;
    let oneTimeTotal = 0;
    const usedCategories = new Set();

    // Build recurring line items from selected capabilities
    Object.entries(cartCapabilities || {}).forEach(([capId, isSelected]) => {
      if (!isSelected) return;
      const cap = STRIPE_CAPABILITY_CATALOG[capId];
      if (!cap) return;

      usedCategories.add(cap.category);

      if (cap.recurring) {
        const qty = fteQty(cap.miraFte, cap.frankFte);
        const dailyCost = cap.unitAmountUsd * qty;
        dailyRecurringTotal += dailyCost;
        lineItems.push({
          id: capId,
          priceId: cap.priceId,
          name: cap.name,
          category: cap.category,
          unitAmountUsd: cap.unitAmountUsd,
          quantity: qty,
          dailyCost,
          monthlyCost: dailyCost * 30,
          recurring: true,
          fteLabel: fteLabel(cap.miraFte, cap.frankFte),
        });
      } else {
        oneTimeTotal += cap.unitAmountUsd;
        lineItems.push({
          id: capId,
          priceId: cap.priceId,
          name: cap.name,
          category: cap.category,
          unitAmountUsd: cap.unitAmountUsd,
          quantity: 1,
          dailyCost: 0,
          monthlyCost: 0,
          recurring: false,
          fteLabel: '',
        });
      }
    });

    // Auto-include setup fees for each used category (same as POC)
    const setupItems = [];
    usedCategories.forEach((category) => {
      const fee = STRIPE_SETUP_FEES[category];
      if (fee) {
        oneTimeTotal += fee.unitAmountUsd;
        setupItems.push({
          id: `setup_${category}`,
          priceId: fee.priceId,
          name: fee.name,
          category,
          unitAmountUsd: fee.unitAmountUsd,
          quantity: 1,
          dailyCost: 0,
          monthlyCost: 0,
          recurring: false,
          fteLabel: '',
          isSetupFee: true,
        });
      }
    });

    const monthlyRecurringTotal = dailyRecurringTotal * 30;
    // Tax applies to the first month's recurring + one-time
    const subtotalDueToday = oneTimeTotal + monthlyRecurringTotal;
    const tax = Math.round(subtotalDueToday * safeTaxRate * 100) / 100;
    const grandTotal = subtotalDueToday + tax;
    // Amount in cents for Stripe
    const grandTotalCents = Math.round(grandTotal * 100);

    return {
      lineItems,
      setupItems,
      allItems: [...lineItems, ...setupItems],
      dailyRecurringTotal,
      monthlyRecurringTotal,
      oneTimeTotal,
      subtotalDueToday,
      tax,
      taxRate: safeTaxRate,
      grandTotal,
      grandTotalCents,
      miraFTE: miraFteNum,
      frankFTE: frankFteNum,
      itemCount: lineItems.length,
      isEmpty: lineItems.length === 0,
    };
  }
);

export const selectSignupData = createSelector(
  [
    (state) => state.signup.selectedOption,
    (state) => state.signup.miraFTE,
    (state) => state.signup.frankFTE,
    (state) => state.signup.organizationName,
    (state) => state.signup.selectedOrgId,
    (state) => state.signup.email,
    (state) => state.signup.officeNumber,
  ],
  (
    selectedOption,
    miraFTE,
    frankFTE,
    organizationName,
    selectedOrgId,
    email,
    officeNumber
  ) => ({
    selectedOption,
    miraFTE,
    frankFTE,
    organizationName,
    selectedOrgId,
    email,
    officeNumber,
  })
);

export default signupSlice.reducer;
